import type { TourFormState, TourFormStepId, TourStepErrors } from '@/features/tour/tour-form-model'
import type { IPOI } from '@/shared/api/types/poi-types'
import { useState } from 'react'
import {
  getTourStepErrors,
  isTourStepUnlocked,
  resolveRouteCoordinates,
} from '@/features/tour/tour-form-model'
import { TOUR_FORM_STEP_IDS } from '@/shared/constants/tour-form'

interface UseTourFormStepsParams {
  isEdit: boolean
  formState: TourFormState
  poiMap: Record<string, IPOI>
  isMediaUploading: boolean
}

export interface TourFormStepItem {
  id: TourFormStepId
  isActive: boolean
  isComplete: boolean
  isUnlocked: boolean
}

/** Which step of the tour form is open, which steps are complete/reachable, and the Back/Next moves between them. */
export function useTourFormSteps({
  isEdit,
  formState,
  poiMap,
  isMediaUploading,
}: UseTourFormStepsParams) {
  const [activeStep, setActiveStep] = useState<TourFormStepId>('content')
  const { translations, basicInfo, selectedPois, routeCoordinates, banner, thumbnail } = formState

  const errors: TourStepErrors = getTourStepErrors({
    translations,
    basicInfo,
    coordinates: resolveRouteCoordinates(routeCoordinates, selectedPois, poiMap),
    banner,
    thumbnail,
    isUploading: isMediaUploading,
  })

  const steps: TourFormStepItem[] = TOUR_FORM_STEP_IDS.map((id) => ({
    id,
    isActive: id === activeStep,
    isComplete: errors[id] === null,
    isUnlocked: isTourStepUnlocked(id, errors, isEdit),
  }))

  const activeIndex = TOUR_FORM_STEP_IDS.indexOf(activeStep)
  const previousStep = TOUR_FORM_STEP_IDS[activeIndex - 1]
  const nextStep = TOUR_FORM_STEP_IDS[activeIndex + 1]

  function goToStep(id: TourFormStepId) {
    if (isTourStepUnlocked(id, errors, isEdit)) setActiveStep(id)
  }

  return {
    steps,
    activeStep,
    /** Why the user cannot leave the current step yet (create only); `null` when they can. */
    activeStepError: errors[activeStep],
    isFirstStep: previousStep === undefined,
    isLastStep: nextStep === undefined,
    goToStep,
    goBack: () => previousStep && setActiveStep(previousStep),
    goNext: () => nextStep && errors[activeStep] === null && goToStep(nextStep),
  }
}
